import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
// Utils
import RealtimeUtil from '../utils/RealtimeUtil';
import { showLoadingToast, showToast } from '../utils/ToastsUtil';
import { isNetworkError, readElementsFromSnapshot, readElementsSnapshotFirst, replaceElementsInSnapshot, saveElementsToSnapshot } from '../utils/offline/OfflineRead';
import { canUseFieldWrite } from '../utils/offline/FieldWrite';
import { createElement, createElements, removeElements, updateElement, updateElements } from '../utils/offline/ElementWrites';
import i18n from '../locales/i18n';
import { isOnline } from '../utils/offline/Connectivity';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class MarkersService {
    static addMarker(marker, action) {
        marker.id = uuidv4();
        marker.properties.markerId = marker.id;
        // Pas de this : méthode passée détachée par ProjectMap
        if (canUseFieldWrite()) return MarkersService.addMarkerThroughOutbox(marker, action);
        const promise = Axios.post(BACKEND_ENDPOINT + 'markers/' + RealtimeUtil.getConnectionId(), marker, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            marker.properties = response.data.properties;
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'marker' + (action === 'copyPasting' ? 'copy_pasting' : action); // TODO Repère : vérifier toasts existent
        const successId = 'marker' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'marker' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static addMarkerThroughOutbox(marker, action) {
        const promise = createElement('Repère', marker).then(response => {
            if (!response.isLocal) marker.properties = response.data.properties;
            return response;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'marker_' + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = 'marker_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'marker_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static addMarkers(markers, action) {
        markers.forEach(m => {
            m.id = uuidv4();
            m.properties.markerId = m.id;
        });
        if (canUseFieldWrite()) return MarkersService.addMarkersThroughOutbox(markers, action);
        const promise = Axios.post(BACKEND_ENDPOINT + 'markers/bulk/' + RealtimeUtil.getConnectionId(), markers, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            response.data.forEach(m => {
                let marker = markers.find(x => x.id === m.id);
                marker.properties = m.properties;
            });
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefix = markers.length > 1 ? 'markers_' : 'marker_'; // TODO Repère : ajouter toasts
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise, markers.length);
        return promise;
    }

    // Comme la réponse de l'API, data contient les repères, complétés par le serveur s'il a répondu à temps
    static addMarkersThroughOutbox(markers, action) {
        const promise = createElements('Repère', markers).then(results => {
            const markersById = new Map(markers.map(marker => [marker.id, marker]));
            results.filter(result => !result.isLocal).flatMap(result => result.data).forEach(serverMarker => {
                const marker = markersById.get(serverMarker.id);
                if (marker) marker.properties = serverMarker.properties;
            });
            return { data: markers, isLocal: results.some(result => result.isLocal) };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const prefix = markers.length > 1 ? 'markers_' : 'marker_';
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise, markers.length);
        return promise;
    }

    static async getMarkers(projectId, data) {
        // Liste complète (ProjectMap.loadMarkers) : copiée en entier et relue sans réseau ; un appel par zone ne fait qu'ajouter
        const isFullList = !data || !Object.keys(data).length;
        const fromSnapshot = isFullList ? await readElementsSnapshotFirst(projectId, 'Repère') : undefined;
        if (fromSnapshot) return { markers: fromSnapshot, missingCustomFieldIds: [] };

        return Axios.post(BACKEND_ENDPOINT + 'markers/projects/' + projectId, data, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (isFullList) replaceElementsInSnapshot(projectId, 'Repère', response.data?.markers);
            else saveElementsToSnapshot(projectId, 'Repère', response.data?.markers);
            return response.data;
        }, async (error) => {
            if (isFullList && isNetworkError(error)) {
                const markers = await readElementsFromSnapshot(projectId, 'Repère');
                if (markers) return { markers, missingCustomFieldIds: [] };
            }
            if (isOnline()) showToast('markers_loading_failed');
            return;
        });
    }

    static removeMarkers(markers) {
        if (canUseFieldWrite()) return MarkersService.removeMarkersThroughOutbox(markers);
        const promise = Axios.delete(BACKEND_ENDPOINT + 'markers/' + RealtimeUtil.getConnectionId(), {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: markers
        }).then(response => {
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefixe = markers.length > 1 ? 'markers' : 'marker';
        showLoadingToast(prefixe + '_deleting', prefixe + '_deleted', prefixe + '_deletion_failed', promise);
        return promise;
    }

    static removeMarkersThroughOutbox(markers) {
        const promise = removeElements('Repère', markers).catch(error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });
        const prefix = markers.length > 1 ? 'markers' : 'marker';
        showLoadingToast(prefix + '_deleting', prefix + '_deleted', prefix + '_deletion_failed', promise);
        return promise;
    }

    static updateMarker(id, marker, action, previousFeature = null) {
        if (canUseFieldWrite()) return MarkersService.updateMarkerThroughOutbox(id, marker, action, previousFeature);
        const promise = Axios.put(BACKEND_ENDPOINT + 'markers/?connectionId=' + RealtimeUtil.getConnectionId() + '&id=' + id, marker, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            marker.properties = { ...marker.properties, ...response.data.properties };
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        // TODO PolygonMarkers
        const toastId = 'marker_' + action;
        const successId = 'marker_' + action.replace('ing', 'ed');
        const errorId = 'marker_' + (action === 'updating' ? 'update' : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static updateMarkerThroughOutbox(id, marker, action, previousFeature) {
        const promise = updateElement('Repère', id, marker, {}, previousFeature).then(response => {
            if (!response.isLocal) marker.properties = { ...marker.properties, ...response.data.properties };
            return response;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'marker_' + action;
        const successId = 'marker_' + action.replace('ing', 'ed');
        const errorId = 'marker_' + (action === 'updating' ? 'update' : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static bulkUpdateMarkers(markers, action, previousFeatures = null) {
        if (canUseFieldWrite()) return MarkersService.bulkUpdateMarkersThroughOutbox(markers, action, previousFeatures);
        const promise = Axios.put(BACKEND_ENDPOINT + 'markers/bulk/' + RealtimeUtil.getConnectionId(), markers, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (Array.isArray(response?.data)) {
                response.data.forEach(feature => {
                    let marker = markers.find(marker => marker.id === feature.id);
                    if (marker) marker.properties = { ...marker.properties, ...feature.properties };
                });
            }
            return response;
        }, (error) => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        // TODO PolygonMarkers
        const toastId = 'markers_' + action;
        const successId = 'markers_' + action.replace('ing', 'ed');
        const errorId = 'markers_' + (action === 'updating' ? 'update' : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    // Réponse locale si un paquet n'est pas accepté dans le délai ; data contient les repères, complétés par le serveur sinon
    static bulkUpdateMarkersThroughOutbox(markers, action, previousFeatures) {
        const promise = updateElements('Repère', markers, {}, previousFeatures).then(results => {
            const byId = new Map(markers.map(marker => [marker.id, marker]));
            results.filter(result => !result.isLocal).flatMap(result => Array.isArray(result.data) ? result.data : []).forEach(serverFeature => {
                const marker = byId.get(serverFeature.id);
                if (marker) marker.properties = { ...marker.properties, ...serverFeature.properties };
            });
            return { data: markers, isLocal: results.some(result => result.isLocal) };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'markers_' + action;
        const successId = 'markers_' + action.replace('ing', 'ed');
        const errorId = 'markers_' + (action === 'updating' ? 'update' : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static exportMarkersFromProjectAsExcel(name, id, elementIds = [], projection) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportXLSX/' + id, { elementIds, projection }, {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id} - ${name} (${i18n.t("Repères").toLowerCase()}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
            return true;
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }

    static exportMarkerAsPDF(marker, image, photosId, colorless, toastCustomTexts) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportPDF/' + marker.id, { image, photosId, colorless }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            let filename = marker.properties.projectReference ? `${i18n.t("Fiche repère")}_${marker.properties.projectReference}` : i18n.t("Fiche repère");
            if (marker.properties.customReference) filename += '_' + marker.properties.customReference;
            filename += '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
        });
    }

    static exportMarkersAsPDF(projectId, elementIds, { projection, colorless = false, addPhotos = true } = {}) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportAllPDF/' + projectId, { elementIds, projection, colorless, addPhotos }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response;
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
            return;
        });
    }

    static exportMarkersAsSHP(projectLabel, projectId, elementIds, projection, geometryType) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportSHP/' + projectId, { elementIds, projection, geometryType }, {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (response.data?.size > 0) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Repères").toLowerCase()} - ${geometryType}).zip`);
                document.getElementById('modal-content').appendChild(link);
                link.click();
                document.getElementById('modal-content').removeChild(link);
                URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_shp_export_failed');
        });
    }

    static exportMarkersAsGPKG(projectLabel, projectId, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'markers/exportGPKG/' + projectId, { projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const blob = new Blob([response.data], { type: 'application/geopackage+sqlite3' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Repères").toLowerCase()}).gpkg`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_gpkg_export_failed');
        });
    }
}
